import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ShieldCheck, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.requestPasswordReset(email.trim());
    } catch (_) {
      /* always show generic success — never leak whether the email exists */
    } finally {
      setBusy(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />
      <div className="flex-1">
        <div className="max-w-md mx-auto px-6 md:px-8 py-14 md:py-20">
          <div className="cre-overline text-sky-700">Account · recovery</div>
          <h1 className="font-heading text-3xl font-black tracking-tight mt-1 text-slate-900">
            Forgot your password?
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Enter the email associated with your CRE account and we'll send
            you a secure link to set a new one. The link is valid for 30
            minutes and can be used only once.
          </p>

          {submitted ? (
            <div
              data-testid="forgot-password-success"
              className="cre-card p-6 mt-8 space-y-3"
            >
              <div className="flex items-start gap-3">
                <Mail
                  className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5"
                  strokeWidth={1.75}
                />
                <div className="text-sm text-slate-700 leading-relaxed">
                  <p>
                    If that email is registered, you'll receive a reset link
                    shortly. Please check your inbox{" "}
                    <span className="text-slate-500">
                      (and spam folder, just in case)
                    </span>
                    .
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    The link expires in 30 minutes. If you don't see the
                    email after a few minutes, you can request another one.
                  </p>
                </div>
              </div>
              <div className="pt-2 border-t border-slate-200 flex items-center justify-between gap-2">
                <Link
                  to="/login"
                  data-testid="forgot-back-to-login"
                  className="text-xs text-sky-700 hover:text-sky-600 underline-offset-4 hover:underline font-semibold flex items-center gap-1"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to sign in
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setSubmitted(false);
                    setEmail("");
                  }}
                  data-testid="forgot-resend"
                  className="text-xs text-slate-500 hover:text-slate-700 underline-offset-4 hover:underline"
                >
                  Use a different email
                </button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="cre-card p-6 mt-8 space-y-4"
              data-testid="forgot-password-form"
            >
              <div>
                <Label className="cre-overline">Email</Label>
                <Input
                  type="email"
                  autoComplete="email"
                  required
                  data-testid="forgot-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 h-11 bg-white border-slate-200 focus-visible:ring-sky-500"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit"
                className="w-full cre-cta-primary rounded-lg font-semibold h-11"
              >
                <Mail className="h-4 w-4 mr-2" strokeWidth={2} />
                {busy ? "Sending link…" : "Send reset link"}
              </Button>
              <div className="text-center text-sm text-slate-600">
                Remembered it?{" "}
                <Link
                  to="/login"
                  data-testid="forgot-goto-login"
                  className="text-sky-700 hover:text-sky-600 underline-offset-4 hover:underline font-semibold"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          )}

          <div className="mt-4 flex items-start gap-2 text-[12px] text-slate-500">
            <ShieldCheck
              className="h-3.5 w-3.5 text-emerald-600 mt-0.5"
              strokeWidth={2}
            />
            <p>
              For your security, we never reveal whether an email is
              registered. The same response is shown for all addresses.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
